import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { DRACOLoader } from "three/addons/loaders/DRACOLoader.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { RGBELoader } from "three/addons/loaders/RGBELoader.js";

// Constants
const EPS = 0.001;

// Initialize DRACOLoader
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath("https://unpkg.com/three@0.167.0/examples/jsm/libs/draco/");

const canvas = document.querySelector("#flexera-3d-canvas");
const scene = new THREE.Scene();

// Set up HDRI loader for environment lighting (optional)
const rgbeLoader = new RGBELoader();
let envLight = null; // Store reference to environment light for intensity control

rgbeLoader.load('https://cdn.shopify.com/s/files/1/0817/2290/0809/files/lightroom_14b.hdr?v=1761298992', (hdrTexture) => {
    hdrTexture.mapping = THREE.EquirectangularReflectionMapping;
    // Set environment for reflections and lighting
    scene.environment = hdrTexture;
    
    // Create environment lighting with controllable intensity
    envLight = new THREE.AmbientLight(0xffffff, 0.3); // Adjust intensity here (0.3 = 30%)
    scene.add(envLight);
    
    // Set default HDRI intensity to 0.1 (10%)
    setTimeout(() => {
        setHDRIIntensity(0.1);
    }, 1000); // Wait 1 second for model to load
    }, undefined, (error) => {
    // HDR loading failed, using default lighting
});

// Store references to all lights for intensity control
let allLights = [];

// Function to control HDRI intensity (affects all lighting)
function setHDRIIntensity(intensity) {
    // Control the environment ambient light
    if (envLight) {
        envLight.intensity = intensity;
    }
    
    // Control all directional lights
    allLights.forEach(light => {
        if (light && light.intensity !== undefined) {
            light.intensity = intensity;
        }
    });
    
    // Control HDRI environment intensity by updating materials
    if (scene.environment) {
        scene.traverse((child) => {
            if (child.isMesh && child.material) {
                if (Array.isArray(child.material)) {
                    child.material.forEach(mat => {
                        if (mat.envMapIntensity !== undefined) {
                            mat.envMapIntensity = intensity;
                        }
                    });
                } else {
                    if (child.material.envMapIntensity !== undefined) {
                        child.material.envMapIntensity = intensity;
                    }
                }
            }
        });
    }
}

// Function to control only environment lighting (keeps directional lights)
function setEnvironmentIntensity(intensity) {
    if (envLight) {
        envLight.intensity = intensity;
    }
    // Environment light not available yet
}

// Function to control only directional lights (keeps environment lighting)
function setDirectionalLightsIntensity(intensity) {
    allLights.forEach(light => {
        if (light && light.intensity !== undefined) {
            light.intensity = intensity;
        }
    });
}

// Function to debug lighting information
function debugLighting() {
    // Debug lighting information removed
}

// Make the functions available globally
window.setHDRIIntensity = setHDRIIntensity;
window.setEnvironmentIntensity = setEnvironmentIntensity;
window.setDirectionalLightsIntensity = setDirectionalLightsIntensity;
window.debugLighting = debugLighting;

// Shadow setup - positioned exactly at product base level
const shadowGeometry = new THREE.PlaneGeometry(15, 15);
// Enhanced shadow material with better cross-browser compatibility
const shadowMaterial = new THREE.ShadowMaterial({ 
    opacity: 0.05,
    transparent: true,
    side: THREE.DoubleSide // Better for Safari
});
const shadowPlane = new THREE.Mesh(shadowGeometry, shadowMaterial);
shadowPlane.rotation.x = -Math.PI / 2;
shadowPlane.position.y = -5; // Positioned exactly at the product base level
shadowPlane.receiveShadow = true;
// Add name for easier debugging
shadowPlane.name = 'shadowPlane';
scene.add(shadowPlane);

// Function to check if camera is in bottom view
function isBottomView() {
    const { phi } = getCurrentAngles();
    // Bottom view is when phi is close to Math.PI (looking up from below)
    // Using a threshold to account for the 0.2 offset in the bottom view function
    return phi > Math.PI - 1.2; // phi > 2.36 radians (about 135 degrees)
}

// Function to update shadow visibility based on camera position
function updateShadowVisibility() {
    if (shadowPlane) {
        const shouldShowShadow = !isBottomView();
        
        // Only update if visibility state has changed to avoid unnecessary updates
        if (shadowPlane.visible !== shouldShowShadow) {
            shadowPlane.visible = shouldShowShadow;
            
            if (shouldShowShadow) {
                // Shadow shown - camera not in bottom view
            } else {
                // Shadow hidden - camera in bottom view
            }
        }
    }
}

// Function to update shadow plane position based on model position
function updateShadowPosition() {
    if (root) {
        // Get the model's bounding box to find its bottom
        const box = new THREE.Box3().setFromObject(root);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        
        // Calculate the bottom of the model
        const modelBottom = center.y - (size.y / 2);
        
        // Position shadow plane slightly below the model bottom
        shadowPlane.position.y = modelBottom - 0;
        
        // Scale shadow plane to match model footprint with better coverage
        // Use a larger multiplier to ensure good coverage even in closed state
        const baseScale = Math.max(size.x, size.z) * 2.5; // 2.5x larger for better closed view coverage
        const minShadowScale = 3.0; // Minimum shadow size to ensure good coverage
        const shadowScale = Math.max(baseScale, minShadowScale);
        shadowPlane.scale.set(shadowScale / 15, shadowScale / 15, 1); // 15 is the original plane size
        
        // Safari-specific shadow adjustments
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (isSafari && dirLight1 && dirLight1.shadow) {
            // Adjust shadow bias for Safari
            dirLight1.shadow.bias = -0.001;
            dirLight1.shadow.normalBias = 0.05;
        }
        
        // Force shadow map update for better cross-browser compatibility
        if (dirLight1 && dirLight1.shadow) {
            dirLight1.shadow.needsUpdate = true;
        }
        
        // Update shadow visibility based on current camera position
        updateShadowVisibility();
    }
}

// Enhanced shadow update function with debouncing for resize events
let shadowUpdateTimeout;
function debouncedUpdateShadow() {
    clearTimeout(shadowUpdateTimeout);
    shadowUpdateTimeout = setTimeout(() => {
        updateShadowPosition();
    }, 50); // 50ms debounce
}

// Function to ensure shadow is properly positioned after any model changes
function ensureShadowPosition() {
    if (root && shadowPlane) {
        updateShadowPosition();
    } else if (!root) {
        // Model not loaded yet, shadow will be updated when model loads
    }
}

const loader = new GLTFLoader();
loader.setDRACOLoader(dracoLoader);

// Suppress GLTFLoader warnings for unsupported UV sets (these are just warnings, not errors)
const originalConsoleWarn = console.warn;
console.warn = function(...args) {
    // Filter out GLTFLoader UV set warnings
    if (args[0] && typeof args[0] === 'string' && args[0].includes('Custom UV set')) {
        return; // Suppress this warning
    }
    originalConsoleWarn.apply(console, args);
};
const mixers = [];
let root;
let animations = {};
let defaultRotation = { x: 0, y: 0, z: 0 };

// Function to fit model to canvas while maintaining aspect ratio
function fitModelToCanvas(model) {
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    
    // Calculate the maximum dimension
    const maxDim = Math.max(size.x, size.y, size.z);
    
    // Calculate scale factor to fit the model in the viewport
    // Use a factor that ensures the model fits well within the camera's view
    const scaleFactor = 2.0 / maxDim; // Reduced scale to show more of the product
    
    model.scale.setScalar(scaleFactor);
    
    // Center the model
    model.position.sub(center.multiplyScalar(scaleFactor));
    
    // Update shadow position after model is fitted
    updateShadowPosition();
}

// Detect environment and set appropriate paths
function detectEnvironment() {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    
    // Check for local development environments
    const isLocal = hostname === 'localhost' ||
                   hostname === '127.0.0.1' ||
                   hostname === '' ||
                   hostname.match(/^172\.(1[6-9]|2[0-9]|3[0-1])\./) || // 172.16.0.0/12 - Private network
                   hostname.match(/^192\.168\./) || // 192.168.0.0/16 - Private network
                   hostname.match(/^10\./) || // 10.0.0.0/8 - Private network
                   hostname.match(/^169\.254\./) || // 169.254.0.0/16 - Link-local
                   protocol === 'file:';
    
    // Determine base URL based on environment
    // Always use current origin to work correctly in both staging and production
    let baseUrl = window.location.origin;
    
    return { isLocal, baseUrl, hostname, protocol };
}

const { isLocal, baseUrl, hostname, protocol } = detectEnvironment();
// Environment detection completed

// Loading progress tracking (optimized)
let lastDisplayedPercentage = -1;

function updateLoadingProgress(progress) {
    try {
        const progressBar = document.getElementById('flexera-progress-bar');
        const progressText = document.getElementById('flexera-progress-text');
        const loadingText = document.getElementById('flexera-loading-text');
        
        if (progressBar && progressText) {
            const percentage = Math.round(progress * 100);
            
            // Only update DOM if percentage actually changed
            if (percentage !== lastDisplayedPercentage) {
                lastDisplayedPercentage = percentage;
                progressBar.style.width = percentage + '%';
                // progressText.textContent = percentage + '%';
                
                // Update loading text based on progress (only when needed)
                if (loadingText) {
                    let newText = '';
                    if (percentage < 30) {
                        newText = 'Loading 3D Model...';
                    } else if (percentage < 70) {
                        newText = 'Processing Geometry...';
                    } else if (percentage < 90) {
                        newText = 'Loading Textures...';
                    } else {
                        newText = 'Finalizing...';
                    }
                    
                    // Only update text if it actually changed
                    if (loadingText.textContent !== newText) {
                        loadingText.textContent = newText;
                    }
                }
            }
        }
    } catch (error) {
        // Error updating loading progress
    }
}

// const modelUrl = 'https://studiox-dev-p2.s3.us-east-1.amazonaws.com/flexera-folding/Flexera_Folding_Web_03.glb';
// const modelUrl = window.flexeraProductData.modelUrl;

let modelUrl;
let modelType = window.flexeraProductData.modelType;
const isMobile = window.innerWidth < 768;

if (isMobile) {
  modelUrl = window.flexeraProductData.modelUrl;
} else {
    modelUrl = window.flexeraProductData.s3modelUrl;
//   modelUrl = 'https://studiox-dev-p2.s3.us-east-1.amazonaws.com/flexera-folding/Flexera_Folding_Web_03.glb';
}
console.log('first_model_src_from_js_modelUrl--------->',modelUrl,"modelUrl-->>",modelType)

// Add this right after loading the model
loader.load(
    modelUrl,
    function (glb) {
        try {
            root = glb.scene;
            // Auto-fit model to canvas while maintaining aspect ratio
            fitModelToCanvas(root);
            
            root.position.x = 0;
            root.position.y = -0.4; // Product sits exactly on the shadow plane
            root.position.z = 0;
            
            // Update shadow position after model is positioned
            updateShadowPosition();
            root.rotation.x = THREE.MathUtils.degToRad(0);
            defaultRotation = {
                x: root.rotation.x,
                y: root.rotation.y,
                z: root.rotation.z,
            };
            scene.add(root);
        } catch (error) {
            // Error processing model
            // Still try to add the model to prevent complete failure
            if (glb && glb.scene) {
                root = glb.scene;
                scene.add(root);
            }
        }

        // Find and handle mainAnim mesh - with comprehensive logging
        let mainAnimMesh = null;
        const allMeshes = [];
        const allObjects = [];
        
        // Model structure analysis
        root.traverse((child) => {
            // Log all objects
            allObjects.push({
                name: child.name,
                type: child.type,
                isMesh: child.isMesh,
                isGroup: child.isGroup,
                isObject3D: child.isObject3D,
                children: child.children ? child.children.length : 0
            });
            
            if (child.isMesh) {
                // Exclude text/logo elements from shadow processing to prevent flickering
                const meshName = child.name.toLowerCase();
                const isTextOrLogo = meshName.includes('text') || 
                                   meshName.includes('logo') || 
                                   meshName.includes('brand') || 
                                   meshName.includes('label') ||
                                   meshName.includes('flexera');
                
                if (!isTextOrLogo) {
                child.castShadow = true;
                child.receiveShadow = true;
                } else {
                    // For text/logo elements, disable shadows to prevent flickering
                    child.castShadow = false;
                    child.receiveShadow = false;
                    // Excluded from shadows
                }
                
                // Collect all mesh info
                allMeshes.push({
                    name: child.name,
                    type: child.type,
                    material: child.material ? child.material.type : 'no material',
                    geometry: child.geometry ? child.geometry.type : 'no geometry',
                    visible: child.visible,
                    position: child.position,
                    rotation: child.rotation,
                    scale: child.scale
                });
                
                // Look for mainAnim mesh (try different variations)
                const animMeshName = child.name.toLowerCase();
                if (animMeshName === 'mainanim' || 
                    animMeshName === 'main_anim' || 
                    animMeshName.includes('main') && animMeshName.includes('anim') ||
                    animMeshName.includes('animation') ||
                    animMeshName.includes('movement') ||
                    animMeshName.includes('frame')) {
                    mainAnimMesh = child;
                    // Found potential animation mesh
                }
            }
        });
        
        // All objects in model
        
        // Log all meshes
        console.log('🎭 ALL MESHES IN MODEL:');
        allMeshes.forEach((mesh, index) => {
            console.log(`${index + 1}. Name: "${mesh.name}" | Material: ${mesh.material} | Geometry: ${mesh.geometry} | Visible: ${mesh.visible}`);
        });
        
        // Log specific search for animation-related meshes
        console.log('🔍 SEARCHING FOR ANIMATION MESHES:');
        const animationMeshes = allMeshes.filter(mesh => 
            mesh.name.toLowerCase().includes('anim') || 
            mesh.name.toLowerCase().includes('motion') ||
            mesh.name.toLowerCase().includes('move') ||
            mesh.name.toLowerCase().includes('frame') ||
            mesh.name.toLowerCase().includes('sequence') ||
            mesh.name.toLowerCase().includes('main')
        );
        
        if (animationMeshes.length > 0) {
            console.log('🎬 Found potential animation meshes:');
            animationMeshes.forEach(mesh => {
                console.log(`  - "${mesh.name}" (${mesh.material}, ${mesh.geometry})`);
            });
        } else {
            console.log('❌ No obvious animation meshes found');
        }
        
        console.log('=== END MODEL ANALYSIS ===');
        
        // Store mainAnim mesh globally for animation control
        window.flexeraMainAnimMesh = mainAnimMesh;

        animations = glb.animations.reduce((acc, clip) => {
            acc[clip.name] = clip;
            return acc;
        }, {});
        
        // Log all available animations
        console.log('🎬 AVAILABLE ANIMATION CLIPS:');
        if (Object.keys(animations).length > 0) {
            Object.keys(animations).forEach((name, index) => {
                const clip = animations[name];
                console.log(`${index + 1}. "${name}" | Duration: ${clip.duration}s | Tracks: ${clip.tracks.length}`);
            });
        } else {
            console.log('❌ No animation clips found in the model');
        }
        
        // Setup mainAnim animation control using AnimationClip
        if (animations.mainAnim) {
            setupMainAnimControls(animations.mainAnim);
        }

        // Log initial model position and rotation
        console.log("Model Position:", root.position);
        console.log("Model Rotation:", root.rotation);
        console.log("Model Scale:", root.scale);
        
        // Store the initial camera settings after model loads
        initialCameraSettings = {
            position: camera.position.clone(),
            target: controls.target.clone(),
            fov: camera.fov
        };

        console.log("Initial camera settings stored:", initialCameraSettings);

        // Now apply device-specific camera settings after model loads
        adjustCameraForDeviceAndOrientation();
        
        // Hide the loader after loading is complete
        const loadingElement = document.getElementById('flexera-3d-loading');
        if (loadingElement) {
            loadingElement.style.display = 'none';
        }
        
    },
    function (progress) {
        // Progress callback - dynamic percentage updates
        const percentage = progress.loaded / progress.total;
        updateLoadingProgress(percentage);
        
        // Log every 10% to reduce console spam
        const roundedPercentage = Math.round(percentage * 100);
        if (roundedPercentage % 10 === 0) {
            console.log('Loading progress:', roundedPercentage + '%');
        }
    },
    function (error) {
        console.error("An error happened during loading:", error);
        // Hide the loader in case of error
        const loadingElement = document.getElementById('flexera-3d-loading');
        if (loadingElement) {
            loadingElement.style.display = 'none';
        }
    },
);

// Lighting setup - reduced intensity for better Shopify compatibility
const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
scene.add(ambientLight);
allLights.push(ambientLight);

// Directional Light 1 - Main shadow casting light
const dirLight1 = new THREE.DirectionalLight(0xffffff, 0.8);
dirLight1.position.set(5, 8, 5); // Adjusted for more direct overhead lighting
dirLight1.castShadow = true;
allLights.push(dirLight1);
// Function to get adaptive shadow resolution
function getAdaptiveShadowResolution() {
    const isMobile = window.innerWidth < 768;
    const isLowEndDevice = navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4;
    const isLowMemory = navigator.deviceMemory && navigator.deviceMemory < 4; // GB
    return (isMobile || isLowEndDevice || isLowMemory) ? 2048 : 4096;
}

// Set initial shadow resolution
const initialShadowResolution = getAdaptiveShadowResolution();
dirLight1.shadow.mapSize.width = initialShadowResolution;
dirLight1.shadow.mapSize.height = initialShadowResolution;
dirLight1.shadow.camera.near = 0.1;
dirLight1.shadow.camera.far = 50;
dirLight1.shadow.camera.left = -12;
dirLight1.shadow.camera.right = 12;
dirLight1.shadow.camera.top = 12;
dirLight1.shadow.camera.bottom = -12;
dirLight1.shadow.bias = -0.0005; // Optimized bias to reduce zigzag artifacts
dirLight1.shadow.normalBias = 0.02; // Additional bias for better shadow quality
dirLight1.shadow.radius = 4; // Soft shadow radius for smoother edges
scene.add(dirLight1);



// Directional Light 2 - Fill light (no shadow casting)
const dirLight2 = new THREE.DirectionalLight(0xffffff, 1);
dirLight2.position.set(-15, 2, -5);
dirLight2.castShadow = false;
scene.add(dirLight2);
allLights.push(dirLight2);

// Directional Light 3
const dirLight3 = new THREE.DirectionalLight(0xffffff, 1);
dirLight3.position.set(-10, 5, 7.5);
dirLight3.castShadow = false;
scene.add(dirLight3);
allLights.push(dirLight3);

// Directional Light 4
const dirLight4 = new THREE.DirectionalLight(0xffffff, 1);
dirLight4.position.set(15, 2, -5);
dirLight4.castShadow = false;
scene.add(dirLight4);
allLights.push(dirLight4);

// Directional Light 5
const dirLight5 = new THREE.DirectionalLight(0xffffff, 1);
dirLight5.position.set(0, 10, 20);
dirLight5.castShadow = false;
scene.add(dirLight5);
allLights.push(dirLight5);

// Directional Light 6
const dirLight6 = new THREE.DirectionalLight(0xffffff, 1);
dirLight6.position.set(0, -5, -10);
dirLight6.castShadow = false;
scene.add(dirLight6);
allLights.push(dirLight6);



// Get canvas dimensions
const getCanvasSize = () => {
    const rect = canvas.getBoundingClientRect();
    return {
        width: rect.width,
        height: rect.height
    };
};

const sizes = getCanvasSize();

const camera = new THREE.PerspectiveCamera(
    25,
    sizes.width / sizes.height,
    0.1,
    1000,
);
camera.position.set(0, 0, 4);
scene.add(camera);

// === COMPREHENSIVE FOV AND POSITION CONFIGURATION SYSTEM ===

// Device and orientation detection
function getDeviceInfo() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const isPortrait = height > width;
    const isLandscape = width > height;

    let deviceType;
    if (width < 768) {
        deviceType = 'mobile';
    } else if (width < 1024) {
        deviceType = 'tablet';
    } else {
        deviceType = 'desktop';
    }

    return {
        width,
        height,
        deviceType,
        orientation: isPortrait ? 'portrait' : 'landscape',
        isPortrait,
        isLandscape,
        aspectRatio: width / height
    };
}

// Comprehensive FOV and position configuration for all scenarios
// Using the EXACT ORIGINAL values that were in the code before
const CAMERA_CONFIG = {
    // Original values from the existing code
    mobile: {
        portrait: {
            normal: { position: { x: 3, y: 0, z: 0 }, fov: 45 },
            fullscreen: { position: { x: 5, y: 0, z: 0 }, fov: 110 }
        },
        landscape: {
            normal: { position: { x:10, y: 0, z: 0 }, fov: 45 },
            fullscreen: { position: { x: 0, y: 0, z: 0 }, fov: 45 }
        }
    },
    tablet: {
        portrait: {
            normal: { position: { x: 2, y: 0, z: 0 }, fov: 45 },
            fullscreen: { position: { x: 0, y: 0, z: 0 }, fov: 45 }
        },
        landscape: {
            normal: { position: { x: 10, y: 0, z: 0 }, fov: 15 },
            fullscreen: { position: { x: 10, y: 0, z: 0 }, fov: 15 }
        }
    },
    desktop: {
        
        landscape: {
            normal: { position: { x: 10, y: 0, z: 0 }, fov: 10 },
            fullscreen: { position: { x: 10, y: 0, z: 0 }, fov: 25 }
        }
    }
};

// Get camera configuration for current device and state
function getCameraConfig(deviceInfo, isFullscreen = false) {
    const deviceConfig = CAMERA_CONFIG[deviceInfo.deviceType];
    if (!deviceConfig) return CAMERA_CONFIG.desktop.landscape.normal;

    const orientationConfig = deviceConfig[deviceInfo.orientation];
    if (!orientationConfig) return deviceConfig.landscape.normal;

    return orientationConfig[isFullscreen ? 'fullscreen' : 'normal'];
}

// Apply camera configuration
function applyCameraConfig(config) {
    camera.position.set(config.position.x, config.position.y, config.position.z);
    camera.fov = config.fov;
    camera.updateProjectionMatrix();
    controls.target.set(0, 0, 0);
    controls.update();
}

// Legacy function for backwards compatibility
function adjustCameraForDevice() {
    adjustCameraForDeviceAndOrientation();
}

// New comprehensive adjustment function
function adjustCameraForDeviceAndOrientation() {
    const deviceInfo = getDeviceInfo();
    const isFullscreen = !!document.fullscreenElement;
    const config = getCameraConfig(deviceInfo, isFullscreen);

    console.log('📱📐 Applying camera config for:', {
        device: deviceInfo.deviceType,
        orientation: deviceInfo.orientation,
        fullscreen: isFullscreen,
        config: config
    });

    applyCameraConfig(config);

    // Update shadow position after camera changes
    if (root) {
        updateShadowPosition();
    }
}

// Initialize with current settings - but only after model loads
// Don't call this immediately as it might interfere with model loading

const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true,
    alpha: true,
});
renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
renderer.setSize(sizes.width, sizes.height);
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.shadowMap.autoUpdate = true;
renderer.shadowMap.needsUpdate = false; // Prevent unnecessary shadow updates

// Configure tone mapping for better color accuracy in different environments
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 0.8; // Reduced exposure to prevent over-saturation

const controls = new OrbitControls(camera, renderer.domElement);

// Store initial camera settings - this will be set after model loads
let initialCameraSettings = null;

// Auto-rotate and directional view settings
let autoRotateEnabled = false;
let autoRotateSpeed = 2.0; // degrees per second - increased for better visibility
let isAnimating = false;

// Predefined camera positions for different views - adjusted for better visibility
const viewPositions = {
    front: { position: new THREE.Vector3(0, 0, 6), target: new THREE.Vector3(0, 0, 0) },
    left: { position: new THREE.Vector3(-4, 0, 0), target: new THREE.Vector3(0, 0, 0) },
    right: { position: new THREE.Vector3(4, 0, 0), target: new THREE.Vector3(0, 0, 0) },
    top: { position: new THREE.Vector3(0, 2, 0), target: new THREE.Vector3(0, 0, 0) }, // Increased height for full view
    bottom: { position: new THREE.Vector3(0, -2, 0), target: new THREE.Vector3(0, 0, 0) }, // Fixed: increased distance and height
    back: { position: new THREE.Vector3(0, 0, -6), target: new THREE.Vector3(0, 0, 0) }
};

// Initial camera settings will be stored after model loads

window.addEventListener("resize", () => {
    const newSizes = getCanvasSize();
    sizes.width = newSizes.width;
    sizes.height = newSizes.height;
    camera.aspect = sizes.width / sizes.height;

    // Apply new camera configuration for current device/orientation
    adjustCameraForDeviceAndOrientation();
    camera.updateProjectionMatrix();

    renderer.setSize(sizes.width, sizes.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

    // Refit model to new canvas size if model is loaded
    if (root) {
        fitModelToCanvas(root);
        // Update shadow position after resize with debouncing
        debouncedUpdateShadow();
    }
});

let lastTime = 0;

function animate(time) {
    requestAnimationFrame(animate);

    const delta = (time - lastTime) / 1000;
    mixers.forEach((mixer) => mixer.update(delta));
    
    // Update mainAnim mixer if it exists
    if (window.flexeraMainAnimState && window.flexeraMainAnimState.mixer) {
        window.flexeraMainAnimState.mixer.update(delta);
    }
    
    // Auto-rotate functionality
    if (autoRotateEnabled && !isAnimating) {
        controls.autoRotate = true;
        controls.autoRotateSpeed = autoRotateSpeed;
    } else {
        controls.autoRotate = false;
    }
    
    // Ensure shadow position is correct (runs only when needed)
    if (root && shadowPlane && time % 1000 < 16) { // Check every ~1 second
        ensureShadowPosition();
    }
    
    // Update shadow visibility based on camera position (runs every frame for smooth transitions)
    if (shadowPlane) {
        updateShadowVisibility();
    }
    
    lastTime = time;

    controls.update();
    renderer.render(scene, camera);
}
animate();

// MainAnim animation control functions using AnimationClip
function setupMainAnimControls(animationClip) {
    console.log('Setting up mainAnim controls for animation clip:', animationClip);
    
    if (!root) {
        console.error('Root object not available for animation setup');
        return;
    }
    
    // Create animation mixer for the root object
    const mixer = new THREE.AnimationMixer(root);
    const action = mixer.clipAction(animationClip);
    
    // Store animation state
    window.flexeraMainAnimState = {
        mixer: mixer,
        action: action,
        clip: animationClip,
        isPlaying: false,
        currentTime: 0,
        duration: animationClip.duration,
        isPaused: false
    };
    
    // Add mixer to the global mixers array for proper updates
    mixers.push(mixer);
    
    // Set to frame 0 (start of animation) and ensure it's ready for manual control
    action.enabled = true;
    action.setEffectiveWeight(1.0);
    action.setEffectiveTimeScale(1.0);
    action.time = 0;
    
    // CRITICAL: Play the action briefly to activate it, then pause
    // This is required for manual control to work
    action.play();
    action.paused = true;
    
    // Force update the mixer to apply initial state
    mixer.update(0.016);
    
    console.log('Animation setup complete:', {
        duration: animationClip.duration,
        actionTime: action.time,
        actionEnabled: action.enabled,
        actionPaused: action.paused
    });
    
    console.log('MainAnim controls initialized:', {
        duration: animationClip.duration,
        tracks: animationClip.tracks.length,
        state: window.flexeraMainAnimState
    });
    
    // Initialize view state to open view (first frame)
    initializeViewState();
}

// Toggle switch functionality for Open/Close View
let isOpenView = true; // Default to open view (first frame)
let isToggleAnimating = false; // Track if toggle animation is currently playing

function toggleView() {
    if (!window.flexeraMainAnimState || !window.flexeraMainAnimState.action) {
        console.warn('Animation state not available');
        return;
    }
    
    if (isToggleAnimating) {
        console.log('Animation already in progress, ignoring toggle');
        return;
    }
    
    const state = window.flexeraMainAnimState;
    isToggleAnimating = true;
    
    // Reset auto-rotate when toggle view is clicked
    resetAutoRotate();
    
    if (isOpenView) {
        // Animate to close view (play from current to last frame)
        console.log('Animating to close view (last frame)');
        animateToCloseView(state);
    } else {
        // Animate to open view (play from current to first frame)
        console.log('Animating to open view (first frame)');
        animateToOpenView(state);
    }
}

function animateToCloseView(state) {
    const startTime = state.action.time;
    const endTime = state.duration - (state.duration / 100); // Stop at 99% of duration (second-to-last frame)
    const duration = 2000; // 2 seconds for full animation
    const startTimestamp = performance.now();
    
    // Keep animation paused and manually control time
    state.action.paused = true;
    
    function updateAnimation() {
        const elapsed = performance.now() - startTimestamp;
        const progress = Math.min(elapsed / duration, 1);
        
        // Interpolate from start time to end time (stopping at second-to-last frame)
        const currentTime = startTime + (endTime - startTime) * progress;
        state.action.time = currentTime;
        state.mixer.update(0.016);
        
        if (progress < 1) {
            requestAnimationFrame(updateAnimation);
        } else {
            // Animation complete - stop at second-to-last frame
            state.action.time = endTime;
            state.mixer.update(0.016);
            isOpenView = false;
            isToggleAnimating = false;
            updateViewButtons();
            console.log('Animation to close view complete (stopped at second-to-last frame)');
        }
    }
    
    updateAnimation();
}

function animateToOpenView(state) {
    const startTime = state.action.time;
    const endTime = 0; // First frame
    const duration = 2000; // 2 seconds for full animation
    const startTimestamp = performance.now();
    
    // Keep animation paused and manually control time
    state.action.paused = true;
    
    function updateAnimation() {
        const elapsed = performance.now() - startTimestamp;
        const progress = Math.min(elapsed / duration, 1);
        
        // Interpolate from start time to end time (reverse)
        const currentTime = startTime - (startTime - endTime) * progress;
        state.action.time = Math.max(currentTime, 0); // Don't go below 0
        state.mixer.update(0.016);
        
        if (progress < 1) {
            requestAnimationFrame(updateAnimation);
        } else {
            // Animation complete
            state.action.time = 0;
            state.mixer.update(0.016);
            isOpenView = true;
            isToggleAnimating = false;
            updateViewButtons();
            console.log('Animation to open view complete');
        }
    }
    
    updateAnimation();
}

function updateViewButtons() {
    const openButton = document.getElementById('flexera-open-View');
    const closeButton = document.getElementById('flexera-close-View');
    
    if (openButton && closeButton) {
        if (isOpenView) {
            // Show close button, hide open button
            openButton.style.display = 'none';
            closeButton.style.display = 'flex';
        } else {
            // Show open button, hide close button
            openButton.style.display = 'flex';
            closeButton.style.display = 'none';
        }
    }
}

// Initialize view state
function initializeViewState() {
    if (window.flexeraMainAnimState) {
        // Set to first frame (open view)
        const state = window.flexeraMainAnimState;
        state.action.time = 0;
        state.action.paused = true;
        state.mixer.update(0.016);
        isOpenView = true;
        updateViewButtons();
        console.log('View state initialized to open view');
    }
}

// Legacy functions for compatibility (now just call toggleView)
function playMainAnim() {
    console.log('playMainAnim called - using toggle functionality');
    toggleView();
}

function stopMainAnim() {
    console.log('stopMainAnim called - using toggle functionality');
    toggleView();
}



function animateCameraTo(targetSettings, duration, callback) {
    const startTime = performance.now();
    const startPosition = camera.position.clone();
    const startTarget = controls.target.clone();
    const startFov = camera.fov;

    function update() {
        const elapsed = performance.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Interpolate position
        camera.position.set(
            interpolate(startPosition.x, targetSettings.position.x, progress),
            interpolate(startPosition.y, targetSettings.position.y, progress),
            interpolate(startPosition.z, targetSettings.position.z, progress),
        );

        // Interpolate target
        controls.target.set(
            interpolate(startTarget.x, targetSettings.target.x, progress),
            interpolate(startTarget.y, targetSettings.target.y, progress),
            interpolate(startTarget.z, targetSettings.target.z, progress),
        );
        camera.fov = startFov;
        camera.updateProjectionMatrix();
        // Interpolate FOV only if it's different
        // if (Math.abs(startFov - targetSettings.fov) > 0.1) {
        //     camera.fov = interpolate(startFov, targetSettings.fov, progress);
        //     camera.updateProjectionMatrix();
        // }

        if (progress < 1) {
            requestAnimationFrame(update);
        } else {
            if (callback) callback();
        }
    }

    update();
}

function interpolate(start, end, progress) {
    return start + (end - start) * progress;
}

// === Spherical/orbit helpers to keep a constant radius (no zoom) ===
function getOrbitRadius() {
  const dx = camera.position.x - controls.target.x;
  const dy = camera.position.y - controls.target.y;
  const dz = camera.position.z - controls.target.z;
  return Math.sqrt(dx*dx + dy*dy + dz*dz);
}
function getCurrentAngles() {
  const tx = controls.target.x, ty = controls.target.y, tz = controls.target.z;
  const cx = camera.position.x - tx;
  const cy = camera.position.y - ty;
  const cz = camera.position.z - tz;
  const r = Math.max(getOrbitRadius(), EPS);
  const theta = Math.atan2(cz, cx);               // azimuth around Y (atan2(z,x))
  const phi   = Math.acos(Math.max(-1, Math.min(1, cy / r))); // polar from +Y
  return { r, theta, phi };
}
function setCameraSpherical(r, theta, phi) {
  const clampedPhi = Math.max(EPS, Math.min(Math.PI - EPS, phi));
  const x = r * Math.cos(theta) * Math.sin(clampedPhi);
  const y = r * Math.cos(clampedPhi);
  const z = r * Math.sin(theta) * Math.sin(clampedPhi);
  camera.position.set(x + controls.target.x, y + controls.target.y, z + controls.target.z);
  controls.update();
}
function easeOutCubic(t){ return 1 - Math.pow(1 - t, 3); }

// Limit the polar angle to allow looking up from below for bottom view
controls.minPolarAngle = 0; // Allow looking straight up
controls.maxPolarAngle = Math.PI; // Allow looking straight down

controls.enablePan = false;
controls.enableZoom = true;
controls.enableKeys = true;
controls.keyPanSpeed = 7.0;

controls.update();

// Color variant functionality
function analyzeColorMeshGroups() {
    try {
        if (!root) {
            return;
        }
        
        const groups = {};
        const allColorMeshes = [];
        
        // Match productColor_ColorName##_hexcode pattern
        const productColorMatcher = /^productColor_([A-Za-z0-9]+)(\d*)_([a-f0-9]{6})$/i;
        
        root.traverse((obj) => {
            if (!obj || !obj.isMesh || !obj.name) return;
            
            const productColorMatch = obj.name.match(productColorMatcher);
            
            if (productColorMatch) {
                const colorName = productColorMatch[1];
                const hexCode = productColorMatch[3];
                
                // Hide this color mesh initially
                obj.visible = false;
                
                allColorMeshes.push({
                    mesh: obj,
                    name: obj.name,
                    colorName: colorName,
                    hexCode: hexCode,
                    type: 'productColor'
                });
            }
        });
        
        // Sort color meshes by their order (optimized)
        allColorMeshes.sort((a, b) => {
            const numA = a.name.match(/(\d+)_[a-f0-9]{6}$/i)?.[1] || "999";
            const numB = b.name.match(/(\d+)_[a-f0-9]{6}$/i)?.[1] || "999";
            return parseInt(numA) - parseInt(numB);
        });
        
        // Group by hex code (optimized)
        const hexCodeGroups = {};
        allColorMeshes.forEach(item => {
            if (item.type === 'productColor' && item.hexCode) {
                const hexKey = item.hexCode.toLowerCase();
                if (!hexCodeGroups[hexKey]) {
                    hexCodeGroups[hexKey] = {
                        colorName: item.colorName,
                        items: []
                    };
                }
                hexCodeGroups[hexKey].items.push(item);
            }
        });
        
        // Create final groups (optimized)
        Object.entries(hexCodeGroups).forEach(([hexCode, data]) => {
            const baseColorName = data.colorName.toLowerCase().replace(/\d+$/, '');
            groups[baseColorName] = {
                prefix: data.colorName,
                names: data.items.map(item => item.name),
                hexCode: `#${hexCode}`
            };
        });
        
        window.flexeraColorMeshGroups = groups;
        
        // Apply default color silently
        applyColor('Ivory');
    } catch (error) {
        console.error('Failed to analyze color mesh groups:', error);
    }
}

function applyColor(colorName) {
    if (!root || !window.flexeraColorMeshGroups) return;
    
    try {
        // Normalize color name - remove spaces and convert to lowercase
        const normalizedColorName = colorName.toLowerCase().replace(/\s+/g, '');
        console.log(`🎨 Trying to apply color: "${colorName}" (normalized: "${normalizedColorName}")`);
        
        // Try multiple variations of the color name
        const possibleKeys = [
            normalizedColorName,
            colorName.toLowerCase(),
            colorName.toLowerCase().replace(/\s+/g, ''),
        ];
        
        console.log(`🎨 Available color groups:`, Object.keys(window.flexeraColorMeshGroups));
        console.log(`🎨 Trying keys:`, possibleKeys);
        
        let showNames = [];
        let matchedKey = null;
        
        // Try to find a matching key
        for (const key of possibleKeys) {
            if (window.flexeraColorMeshGroups[key]) {
                showNames = window.flexeraColorMeshGroups[key].names;
                matchedKey = key;
                console.log(`🎨 Found match with key: "${key}"`);
                break;
            }
        }
        
        // If no exact match, try partial matching
        if (showNames.length === 0) {
            for (const [groupKey, groupData] of Object.entries(window.flexeraColorMeshGroups)) {
                if (groupKey.includes(normalizedColorName) || normalizedColorName.includes(groupKey)) {
                    showNames = groupData.names;
                    matchedKey = groupKey;
                    console.log(`🎨 Found partial match with key: "${groupKey}"`);
                    break;
                }
            }
        }
        
        if (showNames.length === 0) {
            console.warn(`🎨 No color meshes found for: "${colorName}"`);
            return;
        }
        
        // Get all color mesh names to hide non-matching ones
        const allColorMeshNames = new Set();
        Object.values(window.flexeraColorMeshGroups).forEach(group => {
            group.names.forEach(name => allColorMeshNames.add(name));
        });
        
        // Reset color processing flags
        root.traverse(obj => {
            if (obj && obj.isMesh) {
                obj.userData.colorProcessed = false;
            }
        });
        
        // Keep non-color meshes visible
        root.traverse(obj => {
            if (!obj || !obj.isMesh || !obj.name) return;
            
            if (!allColorMeshNames.has(obj.name)) {
                if (obj.userData.originalVisibility === undefined) {
                    obj.userData.originalVisibility = obj.visible;
                }
                obj.visible = obj.userData.originalVisibility;
            }
        });
        
        // Toggle visibility for color meshes
        allColorMeshNames.forEach(meshName => {
            const mesh = root.getObjectByName(meshName);
            if (!mesh) return;
            
            const isInShowNames = showNames.includes(meshName);
            const containsColorName = meshName.toLowerCase().includes(normalizedColorName);
            const show = isInShowNames || (containsColorName && !mesh.userData.colorProcessed);
            
            if (show) {
                mesh.userData.colorProcessed = true;
            }
            
            mesh.visible = show;
        });
        
        window.flexeraCurrentColor = colorName;
        console.log(`🎨 Applied color: ${colorName} (${showNames.length} meshes shown)`);
        
        // Regenerate QR code with new color
        generateQRCode();
        
    } catch (error) {
        console.warn('Failed to apply color:', error);
    }
}

// Setup color variant integration
function setupColorVariantIntegration() {
    // Listen for color swatch changes
    const colorInputs = document.querySelectorAll('input[name="Color"]');
    colorInputs.forEach(input => {
        input.addEventListener('change', (e) => {
            if (e.target.checked) {
                const colorName = e.target.value;
                applyColor(colorName);
            }
        });
    });
    
    // Also listen for variant change events from Shopify
    document.addEventListener('variant:change', (event) => {
        if (event.detail && event.detail.variant) {
            const colorName = event.detail.variant.option1;
            applyColor(colorName);
        }
    });
}

// Centralized control setup to prevent duplicate event listeners
let controlsInitialized = false;

function setupAllControls() {
    if (controlsInitialized) {
        console.log('Controls already initialized, skipping...');
        return;
    }
    
    console.log('Setting up all controls...');
    
    // AR Button
    const arButton = document.getElementById('flexera-ar-button');
    if (arButton) {
        arButton.addEventListener('click', () => {
            // Reset auto-rotate when AR is clicked
            resetAutoRotate();

            // Get current selected color
            const currentColor = window.flexeraCurrentColor || 'Ivory';

            // Detect device for optimal format selection
            const deviceInfo = detectDeviceForAR();
            const modelInfo = getARModelFileName(currentColor, deviceInfo);

            // Enhanced debugging for device detection
            console.log('🔍 AR Button Device Detection Debug:', {
                userAgent: navigator.userAgent,
                userAgentLower: navigator.userAgent.toLowerCase(),
                isIPhone: /iphone/.test(navigator.userAgent.toLowerCase()),
                isIPad: /ipad/.test(navigator.userAgent.toLowerCase()),
                isIPod: /ipod/.test(navigator.userAgent.toLowerCase()),
                isMacintosh: navigator.userAgent.toLowerCase().includes('macintosh'),
                isSafari: navigator.userAgent.toLowerCase().includes('safari'),
                isChrome: navigator.userAgent.toLowerCase().includes('chrome'),
                detectedIOS: deviceInfo.isIOS,
                detectedAndroid: deviceInfo.isAndroid,
                detectedMobile: deviceInfo.isMobile
            });

            console.log('Opening AR view with:', {
                color: currentColor,
                fileName: modelInfo.fileName,
                format: modelInfo.format,
                device: deviceInfo.isIOS ? 'iOS' : (deviceInfo.isAndroid ? 'Android' : 'Other')
            });

            // Open AR with device-specific pages - WITH COLOR PARAMETERS
             if (deviceInfo.isIOS) {
                // iOS devices go to ar-studio.html with color parameter
                window.open(`https://www.joinflexera.com/cdn/shop/t/4/assets/ar-studio.html?color=${encodeURIComponent(currentColor)}`, '_blank');
            } else if (deviceInfo.isAndroid) {
                // Android devices go to flexera-studio.html
                window.open(`https://www.joinflexera.com/cdn/shop/t/4/assets/flexera-studio.html?color=${encodeURIComponent(currentColor)}`, '_blank');
                // window.open(`${window.assetUrls.flexeraPage}?color=${encodeURIComponent(currentColor)}&js=${encodeURIComponent(window.assetUrls.flexera_js)}`,'_blank');
            } else {
                // Desktop/other devices go to ar-studio.html with color parameter
                window.open(`https://www.joinflexera.com/cdn/shop/t/4/assets/ar-studio.html?color=${encodeURIComponent(currentColor)}`, '_blank');
            }
        });
    }
    
    // Reset View Button
    const resetButton = document.getElementById('flexera-reset-view');
    if (resetButton) {
        resetButton.addEventListener('click', () => {
            resetView();
        });
    }
    
    // Fullscreen Button
    const fullscreenButton = document.getElementById('flexera-fullscreen');
    if (fullscreenButton) {
        fullscreenButton.addEventListener('click', () => {
            toggleFullscreen();
        });
    }
    
    // Animation Control Buttons (legacy - now handled by toggle)
    const playButton = document.getElementById('flexera-play-anim');
    const stopButton = document.getElementById('flexera-stop-anim');
    
    if (playButton) {
        playButton.addEventListener('click', toggleView);
    }
    
    if (stopButton) {
        stopButton.addEventListener('click', toggleView);
    }
    
    // Auto-rotate button
    const autoRotateButton = document.getElementById('flexera-auto-rotate');
    if (autoRotateButton) {
        autoRotateButton.addEventListener('click', () => {
            const isEnabled = toggleAutoRotate();
            // Update visual state with CSS classes
            if (isEnabled) {
                autoRotateButton.classList.add('auto-rotate-active');
                autoRotateButton.classList.remove('auto-rotate-inactive');
            } else {
                autoRotateButton.classList.add('auto-rotate-inactive');
                autoRotateButton.classList.remove('auto-rotate-active');
            }
        });
    }
    
    // Directional view buttons
    const topViewButton = document.getElementById('flexera-top-view');
    const leftViewButton = document.getElementById('flexera-left-view');
    const rightViewButton = document.getElementById('flexera-right-view');
    const bottomViewButton = document.getElementById('flexera-bottom-view');
    
    if (topViewButton) {
        topViewButton.addEventListener('click', goToTopView);
    }
    
    if (leftViewButton) {
        leftViewButton.addEventListener('click', goToLeftView);
    }
    
    if (rightViewButton) {
        rightViewButton.addEventListener('click', goToRightView);
    }
    
    if (bottomViewButton) {
        bottomViewButton.addEventListener('click', goToBottomView);
    }
    
    // Open/Close View toggle buttons
    const openViewButton = document.getElementById('flexera-open-View');
    const closeViewButton = document.getElementById('flexera-close-View');
    
    if (openViewButton) {
        openViewButton.addEventListener('click', toggleView);
    }
    
    if (closeViewButton) {
        closeViewButton.addEventListener('click', toggleView);
    }
    
    // Initialize button visibility
    updateViewButtons();
    
    // Initialize auto-rotate button state
    if (autoRotateButton) {
        autoRotateButton.classList.add('auto-rotate-inactive');
        autoRotateButton.classList.remove('auto-rotate-active');
    }
    
    controlsInitialized = true;
    console.log('All controls initialized successfully');
}

// Legacy function for compatibility
function setupControls() {
    setupAllControls();
}

function resetView() {
    // Reset camera to device-specific initial position
    const deviceInfo = getDeviceInfo();
    const isFullscreen = !!document.fullscreenElement;
    const config = getCameraConfig(deviceInfo, isFullscreen);
    
    // Create settings object for animation
    const resetSettings = {
        position: new THREE.Vector3(config.position.x, config.position.y, config.position.z),
        target: new THREE.Vector3(0, 0, 0),
        fov: config.fov
    };
    
    animateCameraTo(resetSettings, 800);
    console.log('Camera reset to device-specific initial position:', {
        device: deviceInfo.deviceType,
        orientation: deviceInfo.orientation,
        fullscreen: isFullscreen,
        config: resetSettings
    });
    
    // Reset auto-rotate to inactive state
    autoRotateEnabled = false;
    controls.autoRotate = false;
    
    // Reset auto-rotate button visual state
    const autoRotateButton = document.getElementById('flexera-auto-rotate');
    if (autoRotateButton) {
        autoRotateButton.classList.add('auto-rotate-inactive');
        autoRotateButton.classList.remove('auto-rotate-active');
    }
    
    // Reset view to open state if currently in close view
    if (!isOpenView && window.flexeraMainAnimState) {
        const state = window.flexeraMainAnimState;
        state.action.time = 0;
        state.action.paused = true;
        state.mixer.update(0.016);
        isOpenView = true;
        updateViewButtons();
        console.log('View reset to open state');
    }
    
    console.log('Complete reset performed - camera, auto-rotate, and view state');
}

// Auto-rotate control functions
function toggleAutoRotate() {
    autoRotateEnabled = !autoRotateEnabled;
    console.log('Auto-rotate:', autoRotateEnabled ? 'enabled' : 'disabled');
    return autoRotateEnabled;
}

function setAutoRotateSpeed(speed) {
    autoRotateSpeed = Math.max(0.1, Math.min(5.0, speed));
    console.log('Auto-rotate speed set to:', autoRotateSpeed);
}

// Helper function to calculate circular positions around the product
function getCircularPosition(angle, distance = 6, height = 0) {
    const r = getOrbitRadius();
    const x = Math.cos(angle) * r;
    const z = Math.sin(angle) * r;
    return new THREE.Vector3(x, camera.position.y, z);
  }

// Get current camera angle around the product
function getCurrentCameraAngle() {
    const currentPos = camera.position;
    return Math.atan2(currentPos.z, currentPos.x);
}

// Directional view functions with smooth circular rotation
function goToView(viewName) {
  if (isAnimating) return;
  const planarViews = ['left','right','front','back'];
  isAnimating = true;
  
  // Reset auto-rotate when any directional view is clicked
  resetAutoRotate();

  if (planarViews.includes(viewName)) {
    if ((viewName === 'left' || viewName === 'right') && isPolarView()) {
      rotateFromPolarToFrontThenSide(viewName);
    } else {
      animateCircularRotation(viewName, 800);
    }
  } else if (viewName === 'top') {
    rotateToFrontThenPitch('top');
  } else if (viewName === 'bottom') {
    rotateToFrontThenPitch('bottom');
  } else {
    isAnimating = false;
    console.warn('Unknown view:', viewName);
  }
}

// Animate circular rotation around the product
function animateCircularRotation(viewName, duration) {
    const startTime = performance.now();
    const startAngle = getCurrentCameraAngle();
    
    // Calculate target angle based on view
    let targetAngle;
    switch (viewName) {
        case 'front': targetAngle = 0; break;
        case 'right': targetAngle = -Math.PI * 0.5; break;
        case 'back': targetAngle = Math.PI; break;
        case 'left': targetAngle = Math.PI * 0.5; break;
        default: targetAngle = startAngle;
    }
    
    // Calculate the shortest rotation path
    let angleDiff = targetAngle - startAngle;
    if (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
    if (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;
    
    const finalAngle = startAngle + angleDiff;
    
    function update() {
        const elapsed = performance.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Use easing function for smooth rotation
        const easedProgress = 1 - Math.pow(1 - progress, 3); // easeOutCubic
       
        // Update camera position
        const cur = getCurrentAngles();
const currentAngle = startAngle + (angleDiff * easedProgress);
setCameraSpherical(cur.r, currentAngle, cur.phi);
controls.target.set(0, 0, 0);
controls.update();
        
        if (progress < 1) {
            requestAnimationFrame(update);
        } else {
            isAnimating = false;
            // Update shadow visibility after camera movement is complete
            updateShadowVisibility();
            console.log('Camera rotated to', viewName, 'view');
        }
    }
    
    update();
}
function rotateToFrontThenPitch(direction) {
  const { r, theta, phi } = getCurrentAngles();
  const frontAngle = 0; // our system defines "front" at theta = 0

  // Step 1: shortest azimuth rotation to front
  let angleDiff = frontAngle - theta;
  if (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
  if (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

  const startTime = performance.now();
  const rotDuration = 700;

  function rotateStep() {
    const t = Math.min((performance.now() - startTime) / rotDuration, 1);
    const eased = easeOutCubic(t);
    const curAngle = theta + angleDiff * eased;
    setCameraSpherical(r, curAngle, phi);  // keep elevation
    controls.target.set(0, 0, 0);
    controls.update();

    if (t < 1) {
      requestAnimationFrame(rotateStep);
    } else {
      // Step 2: pitch up (top) or down (bottom) without changing radius
      const pitchDuration = 700;
      const pitchStart = performance.now();
      const startPhi = getCurrentAngles().phi;
      const topPhi = 0.2;            // nearly overhead (avoid exact 0)
      const bottomPhi = Math.PI - 0.2;
      const targetPhi = direction === 'top' ? topPhi : bottomPhi;

      (function pitchStep(){
        const tp = Math.min((performance.now() - pitchStart) / pitchDuration, 1);
        const ease = easeOutCubic(tp);
        setCameraSpherical(r, frontAngle, startPhi + (targetPhi - startPhi) * ease);
        controls.target.set(0, 0, 0);
        controls.update();

        if (tp < 1) {
          requestAnimationFrame(pitchStep);
        } else {
          isAnimating = false;
          // Update shadow visibility after camera movement is complete
          updateShadowVisibility();
          console.log('Camera moved to', direction, 'view via front pivot');
        }
      })();
    }
  }
  rotateStep();
}

// === Polar/planar helpers for smooth transition from top/bottom to left/right ===
function isPolarView() {
  // Consider near-top or near-bottom as polar
  const phi = getCurrentAngles().phi;
  const threshold = 0.35; // must match the top/bottom offsets used elsewhere
  return (phi <= threshold) || (phi >= Math.PI - threshold);
}

function rotateFromPolarToFrontThenSide(side) {
  const sideAngle = (side === 'right') ? -Math.PI * 0.5 : Math.PI * 0.5; // right: -90°, left: +90°
  const { r } = getCurrentAngles();
  const frontAngle = 0;

  // Step 1: pitch from polar to neutral/front (equatorial) without changing radius
  const pitchDuration = 600;
  const startPitch = performance.now();
  const startPhi = getCurrentAngles().phi;
  const neutralPhi = Math.PI * 0.5; // equatorial

  function pitchStep(){
    const t = Math.min((performance.now() - startPitch) / pitchDuration, 1);
    const eased = easeOutCubic(t);
    const curPhi = startPhi + (neutralPhi - startPhi) * eased;
    setCameraSpherical(r, frontAngle, curPhi); // keep theta at front while pitching
    controls.target.set(0, 0, 0);
    controls.update();
    if (t < 1) { requestAnimationFrame(pitchStep); }
    else {
      // Step 2: rotate azimuth from front to side
      const rotDuration = 700;
      const start = performance.now();
      const cur = getCurrentAngles();
      const startTheta = cur.theta; // should be ~0 after pitch
      let angleDiff = sideAngle - startTheta;
      if (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
      if (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

      (function rotStep(){
        const tr = Math.min((performance.now() - start) / rotDuration, 1);
        const ease = easeOutCubic(tr);
        const thetaNow = startTheta + angleDiff * ease;
        setCameraSpherical(r, thetaNow, neutralPhi);
        controls.target.set(0, 0, 0);
        controls.update();
        if (tr < 1) requestAnimationFrame(rotStep); else {
          isAnimating = false;
          // Update shadow visibility after camera movement is complete
          updateShadowVisibility();
          console.log('Camera moved from polar to', side, 'via front');
        }
      })();
    }
  }

  pitchStep();
}

// Function to reset auto-rotate and update UI
function resetAutoRotate() {
    controls.autoRotate = false;
    autoRotateEnabled = false;
    
    // Reset auto-rotate button visual state
    const autoRotateButton = document.getElementById('flexera-auto-rotate');
    if (autoRotateButton) {
        autoRotateButton.classList.add('auto-rotate-inactive');
        autoRotateButton.classList.remove('auto-rotate-active');
    }
    
    console.log('Auto-rotate reset');
}

function goToLeftView() { goToView('left'); }
function goToRightView() { goToView('right'); }
function goToTopView() { 
    if (!isAnimating) {
        resetAutoRotate();
        rotateToFrontThenPitch('top'); 
    }
}
function goToBottomView() { 
    if (!isAnimating) {
        resetAutoRotate();
        rotateToFrontThenPitch('bottom'); 
    }
}
function goToFrontView() { goToView('front'); }
function goToBackView() { goToView('back'); }

function toggleFullscreen() {
    // Reset auto-rotate when fullscreen is toggled
    resetAutoRotate();
    
    const container = document.getElementById('flexera-3d-viewer');
    
    if (!document.fullscreenElement) {
        if (container.requestFullscreen) {
            container.requestFullscreen();
        } else if (container.webkitRequestFullscreen) {
            container.webkitRequestFullscreen();
        } else if (container.msRequestFullscreen) {
            container.msRequestFullscreen();
        }
    } else {
        if (document.exitFullscreen) {
            document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
        }
    }
}

// Enhanced fullscreen handling with comprehensive device/orientation support
function handleFullscreenChange() {
    // Check current fullscreen state to determine delay
    const wasFullscreen = !!document.fullscreenElement;
    // Use a longer delay for exiting fullscreen to ensure dimensions are fully updated
    const delay = wasFullscreen ? 300 : 100; // Longer delay when exiting

    setTimeout(() => {
        const isFullscreen = !!document.fullscreenElement;

        // Re-detect device info after fullscreen state change (dimensions may have changed)
        const deviceInfo = getDeviceInfo();
        const config = getCameraConfig(deviceInfo, isFullscreen);

        console.log('📺📱 Fullscreen change detected:', {
            isFullscreen,
            device: deviceInfo.deviceType,
            orientation: deviceInfo.orientation,
            dimensions: `${deviceInfo.width}x${deviceInfo.height}`,
            containerSize: document.getElementById('flexera-3d-viewer')?.getBoundingClientRect(),
            windowSize: `${window.innerWidth}x${window.innerHeight}`,
            delay: delay
        });

        if (isFullscreen) {
            // Entering fullscreen: scale model 2x and apply fullscreen config
            if (root) {
                root.scale.set(2, 2, 2); // Scale model 2x in fullscreen
                root.position.y = -0.4; // Ensure model sits on shadow plane
            }

            // Apply fullscreen configuration
            applyCameraConfig(config);

            // Update shadow position to match model scale and position
            updateShadowPosition();

            // Update controls target
            controls.target.set(0, 0, 0);
            controls.update();

            // Resize renderer to full screen dimensions
            const width = window.innerWidth;
            const height = window.innerHeight;
            renderer.setSize(width, height);
            camera.aspect = width / height;
            camera.updateProjectionMatrix();

            console.log('📺📱 Entered fullscreen:', {
                device: deviceInfo.deviceType,
                orientation: deviceInfo.orientation,
                config: config,
                modelScale: 2
            });
        } else {
            // Exiting fullscreen: restore original scale and apply normal config
            console.log('📺📱 Starting fullscreen exit process...');

            if (root) {
                root.scale.set(1, 1, 1); // Restore original scale
                root.position.y = -0.4; // Ensure model sits on shadow plane
                console.log('📺📱 Model scale restored to 1x');
            }

            // Apply normal configuration
            console.log('📺📱 Applying normal config:', config);
            applyCameraConfig(config);

            // Update shadow position to match restored model scale and position
            updateShadowPosition();

            // Update controls target
            controls.target.set(0, 0, 0);
            controls.update();

            // Resize renderer back to container dimensions with fallback
            const container = document.getElementById('flexera-3d-viewer');
            if (container) {
                const rect = container.getBoundingClientRect();
                console.log('📺📱 Container dimensions:', rect);
                renderer.setSize(rect.width, rect.height);
                camera.aspect = rect.width / rect.height;
                camera.updateProjectionMatrix();
                console.log('📺📱 Renderer resized to container');
            } else {
                // Fallback to window dimensions if container not found
                console.warn('📺📱 Container not found, using window dimensions');
                renderer.setSize(window.innerWidth, window.innerHeight);
                camera.aspect = window.innerWidth / window.innerHeight;
                camera.updateProjectionMatrix();
            }

            // Force a model refit to ensure proper sizing after scale change
            if (root) {
                console.log('📺📱 Refitting model to canvas...');
                fitModelToCanvas(root);
                // Ensure model is properly positioned after refit
                root.position.x = 0;
                root.position.y = -0.4;
                root.position.z = 0;
                console.log('📺📱 Model refit complete');
            }

            console.log('📺📱 Exited fullscreen:', {
                device: deviceInfo.deviceType,
                orientation: deviceInfo.orientation,
                config: config,
                modelScale: 1,
                cameraPosition: camera.position,
                cameraFov: camera.fov
            });
        }
    }, 100);
}

// Add fullscreen change event listeners for all browsers
document.addEventListener('fullscreenchange', handleFullscreenChange);
document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
document.addEventListener('mozfullscreenchange', handleFullscreenChange);
document.addEventListener('MSFullscreenChange', handleFullscreenChange);

// Keyboard controls for camera movement and fullscreen handling
function setupKeyboardControls() {
    document.addEventListener('keydown', (event) => {
        if (isAnimating) return; // Don't allow keyboard control during camera animations

        // Handle Escape key for fullscreen exit
        if (event.key === 'Escape' || event.keyCode === 27) {
            if (document.fullscreenElement || document.webkitFullscreenElement ||
                document.mozFullScreenElement || document.msFullscreenElement) {
                console.log('⌨️ Escape key pressed while in fullscreen - will trigger fullscreen exit');
                // The browser will handle the fullscreen exit and trigger our event listeners
                return;
            }
        }
        
        const moveSpeed = 0.1;
        const rotateSpeed = 0.05;
        
        switch(event.code) {
            case 'ArrowUp':
                event.preventDefault();
                camera.position.y += moveSpeed;
                controls.target.y += moveSpeed;
                break;
            case 'ArrowDown':
                event.preventDefault();
                camera.position.y -= moveSpeed;
                controls.target.y -= moveSpeed;
                break;
            case 'ArrowLeft':
                event.preventDefault();
                camera.position.x -= moveSpeed;
                controls.target.x -= moveSpeed;
                break;
            case 'ArrowRight':
                event.preventDefault();
                camera.position.x += moveSpeed;
                controls.target.x += moveSpeed;
                break;
            case 'KeyW':
                event.preventDefault();
                camera.position.z -= moveSpeed;
                controls.target.z -= moveSpeed;
                break;
            case 'KeyS':
                event.preventDefault();
                camera.position.z += moveSpeed;
                controls.target.z += moveSpeed;
                break;
        }
        
        controls.update();
    });
}

// Dynamic shadow resolution adaptation
function adaptShadowResolution() {
    const newResolution = getAdaptiveShadowResolution();
    const currentResolution = dirLight1.shadow.mapSize.width;
    
    if (newResolution !== currentResolution) {
        console.log(`Adapting shadow resolution: ${currentResolution} → ${newResolution}`);
        dirLight1.shadow.mapSize.width = newResolution;
        dirLight1.shadow.mapSize.height = newResolution;
        dirLight1.shadow.map?.dispose(); // Dispose old shadow map
        dirLight1.shadow.needsUpdate = true; // Force shadow map regeneration
    }
}

// Listen for window resize and orientation changes
window.addEventListener('resize', () => {
    setTimeout(() => {
        adaptShadowResolution();
        // Also update camera for new device/orientation
        adjustCameraForDeviceAndOrientation();
    }, 100); // Debounce resize events
});

window.addEventListener('orientationchange', () => {
    setTimeout(() => {
        adaptShadowResolution();
        // Apply camera settings for new orientation immediately
        adjustCameraForDeviceAndOrientation();
        console.log('📱📐 Orientation changed - camera settings updated');
    }, 200); // Wait for orientation change to complete
});

// Enhanced device detection for AR format selection
function detectDeviceForAR() {
    const userAgent = navigator.userAgent.toLowerCase();
    
    // Enhanced iOS detection - includes Safari on macOS (supports AR Quick Look)
    const isIOS = /iphone|ipad|ipod/.test(userAgent) ||
        (userAgent.includes('macintosh') && userAgent.includes('safari') && !userAgent.includes('chrome'));
    
    const isAndroid = /android/.test(userAgent);
    const isMobile = /mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    
    return {
        isIOS: isIOS,
        isAndroid: isAndroid,
        isMobile: isMobile,
        userAgent: userAgent
    };
}

// Color to file mapping function with format preference
function getARModelFileName(colorName, deviceInfo) {
    const colorMapping = {
        'ivory': {
            glb: 'Flexera_Folding_Ivory.glb',
            usdz: 'Flexera_AR_Ivory.usdz'
        },
        'Charcoal': {
            glb: 'Flexera_Folding_Black.glb',
            usdz: 'Flexera_AR_Black.usdz'
        },
        'mocha': {
            glb: 'Flexera_Folding_Mocha.glb',
            usdz: 'Flexera_AR_Mocha.usdz'
        },
        'sagegreen': {
            glb: 'Flexera_Folding_Green.glb',
            usdz: 'Flexera_AR_Green.usdz'
        },
        'pink': {
            glb: 'Flexera_Folding_Pink.glb',
            usdz: 'Flexera_AR_Pink.usdz'
        },
        'green': {
            glb: 'Flexera_Folding_Green.glb',
            usdz: 'Flexera_AR_Green.usdz'
        }
    };


    // Normalize color name for lookup
    const normalizedColor = colorName.toLowerCase().replace(/\s+/g, '');
    const colorData = colorMapping[normalizedColor] || {
        glb: modelUrl,
        usdz: '/usdz/Flexera.usdz'
    };

    // Choose format based on device
    if (deviceInfo.isIOS) {
        // iOS prefers USDZ for better performance and AR compatibility
        return {
            fileName: colorData.usdz,
            format: 'usdz',
            mimeType: 'model/vnd.usdz+zip'
        };
    } else {
        // Android and other devices use GLB
        return {
            fileName: colorData.glb,
            format: 'glb',
            mimeType: 'model/gltf-binary'
        };
    }
}

// Legacy function for backward compatibility
function getGLBFileName(colorName) {
    const deviceInfo = detectDeviceForAR();
    const modelInfo = getARModelFileName(colorName, deviceInfo);
    return modelInfo.fileName;
}

// QR Code generation function
function generateQRCode() {
    const qrContainer = document.getElementById('flexera-qr-code-container');
    if (!qrContainer) {
        console.error('QR code container not found');
        return;
    }

    // Check if qrcode library is available
    if (typeof qrcode === 'undefined') {
        console.error('qrcode library not loaded');
        qrContainer.innerHTML = '<div style="width: 100px; height: 100px; background: #f0f0f0; display: flex; align-items: center; justify-content: center; font-size: 10px; text-align: center; border: 1px solid #ccc;">QR Library Loading...</div>';
        return;
    }

    // Get current color
    const currentColor = window.flexeraCurrentColor || 'Ivory';

    // Use the same environment detection function
    const { isLocal: isLocalQR, baseUrl } = detectEnvironment();

    // Always point to ar-studio.html by default (it will handle device detection and redirect Android to flexera-studio.html)
    const arUrl = `https://www.joinflexera.com/cdn/shop/t/4/assets/ar-studio.html?color=${encodeURIComponent(currentColor)}`;

    console.log('Generating QR code for URL with current color:', {
        url: arUrl,
        color: currentColor,
        note: 'AR page will start with current color selection'
    });

    // Clear previous QR code
    qrContainer.innerHTML = '';

    try {
        // Generate QR code using qrcode-generator library
        const qr = qrcode(0, 'M');
        qr.addData(arUrl);
        qr.make();

        // Create canvas element
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = 100;
        canvas.height = 100;

        // Get QR code modules
        const modules = qr.getModuleCount();
        const cellSize = Math.floor(100 / modules);
        const offset = Math.floor((100 - modules * cellSize) / 2);

        // Draw QR code
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, 100, 100);

        ctx.fillStyle = '#000000';
        for (let row = 0; row < modules; row++) {
            for (let col = 0; col < modules; col++) {
                if (qr.isDark(row, col)) {
                    ctx.fillRect(
                        offset + col * cellSize,
                        offset + row * cellSize,
                        cellSize,
                        cellSize
                    );
                }
            }
        }

        // Add canvas to container
        qrContainer.appendChild(canvas);
        console.log('QR Code generated successfully for color:', currentColor);

    } catch (e) {
        console.error('QR Code generation failed:', e);
        qrContainer.innerHTML = '<div style="width: 100px; height: 100px; background: #f0f0f0; display: flex; align-items: center; justify-content: center; font-size: 10px; text-align: center; border: 1px solid #ccc;">QR Error</div>';
    }
}

// Initialize the viewer
document.addEventListener('DOMContentLoaded', () => {
    // Setup controls immediately
    setupAllControls();
    setupKeyboardControls();
    
    // Wait for QR code library to load, then generate QR code
    const waitForQRCodeLibrary = () => {
        console.log('Checking for QR code library...', typeof qrcode);
        if (typeof qrcode !== 'undefined') {
            console.log('QR code library found, generating QR code...');
            generateQRCode();
        } else {
            console.log('Waiting for QR code library to load...');
            setTimeout(waitForQRCodeLibrary, 100);
        }
    };
    
    // Start waiting for QR code library
    setTimeout(waitForQRCodeLibrary, 500);
    
    // Wait for model to load before setting up color integration
    // Check if model is loaded before analyzing color groups
    const checkModelAndSetup = () => {
        if (root) {
            analyzeColorMeshGroups();
            setupColorVariantIntegration();
        } else {
            // If model not loaded yet, wait a bit more
            setTimeout(checkModelAndSetup, 500);
        }
    };
    
    // Start checking after a short delay
    setTimeout(checkModelAndSetup, 1000);
});

// === UTILITY FUNCTIONS FOR TESTING AND DEBUGGING ===

// Get current device and orientation info
function getCurrentDeviceInfo() {
    return getDeviceInfo();
}

// Get current camera configuration
function getCurrentCameraConfig() {
    const deviceInfo = getDeviceInfo();
    const isFullscreen = !!document.fullscreenElement;
    return getCameraConfig(deviceInfo, isFullscreen);
}

// Force apply camera configuration (useful for testing)
function forceCameraUpdate() {
    adjustCameraForDeviceAndOrientation();
    console.log('🔄 Camera configuration force updated');
}

// Log all available camera configurations
function logCameraConfigurations() {
    console.log('📱📐 CAMERA_CONFIG:', CAMERA_CONFIG);
    console.log('📱 Current device info:', getCurrentDeviceInfo());
    console.log('📷 Current camera config:', getCurrentCameraConfig());
}

// Test all device/orientation combinations
function testAllConfigurations() {
    const devices = ['mobile', 'tablet', 'desktop'];
    const orientations = ['portrait', 'landscape'];
    const states = ['normal', 'fullscreen'];

    console.log('🧪 Testing all camera configurations...');

    devices.forEach(device => {
        orientations.forEach(orientation => {
            states.forEach(state => {
                const mockDeviceInfo = { deviceType: device, orientation };
                const config = getCameraConfig(mockDeviceInfo, state === 'fullscreen');
                console.log(`${device} ${orientation} ${state}:`, config);
            });
        });
    });
}

// Test fullscreen exit (useful for debugging)
function testFullscreenExit() {
    if (document.fullscreenElement || document.webkitFullscreenElement ||
        document.mozFullScreenElement || document.msFullscreenElement) {
        console.log('🧪 Testing fullscreen exit...');
        if (document.exitFullscreen) {
            document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
            document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
        }
    } else {
        console.log('❌ Not in fullscreen mode');
    }
}

// Force restore to initial camera settings (fallback for fullscreen issues)
function forceRestoreInitialCamera() {
    console.log('🔄 Force restoring initial camera settings...');

    // Use device-specific configuration instead of stored settings
    const deviceInfo = getDeviceInfo();
    const isFullscreen = !!document.fullscreenElement;
    const config = getCameraConfig(deviceInfo, isFullscreen);
    
    console.log('📷 Restoring to device-specific settings:', {
        device: deviceInfo.deviceType,
        orientation: deviceInfo.orientation,
        fullscreen: isFullscreen,
        config: config
    });
    
    camera.position.set(config.position.x, config.position.y, config.position.z);
    controls.target.set(0, 0, 0);
    camera.fov = config.fov;
    camera.updateProjectionMatrix();
    controls.update();

    // Ensure model is properly scaled
    if (root) {
        root.scale.set(1, 1, 1);
        root.position.y = -0.4;
        fitModelToCanvas(root);
    }

    console.log('✅ Camera settings force restored');
}

// Export functions for external use
window.flexera3DViewer = {
    applyColor,
    analyzeColorMeshGroups,
    setupColorVariantIntegration,
    generateQRCode,
    toggleView,
    playMainAnim,
    stopMainAnim,
    toggleAutoRotate,
    setAutoRotateSpeed,
    goToLeftView,
    goToRightView,
    goToTopView,
    goToBottomView,
    goToFrontView,
    goToBackView,
    // New utility functions
    getCurrentDeviceInfo,
    getCurrentCameraConfig,
    forceCameraUpdate,
    logCameraConfigurations,
    testAllConfigurations,
    adjustCameraForDeviceAndOrientation,
    testFullscreenExit,
    forceRestoreInitialCamera
};



    // Add event listeners to color swatches to update the QR code text
    document.addEventListener('DOMContentLoaded', function () {
      const colorInputs = document.querySelectorAll('input[name="Color"]');

      colorInputs.forEach(input => {
        input.addEventListener('change', function () {
          if (this.checked) {
            const selectedColor = this.value;
            const qrCodeText = document.querySelector('.flexera-qr-code-text');
            const arText = document.querySelector('.flexera-ar-text');

            if (qrCodeText) {
              // Update the text to include the selected color
              const baseText = 'Scan to see in your space';
              const colorText = selectedColor.toLowerCase();

              // Capitalize first letter for better readability
              const capitalizedColor = colorText.charAt(0).toUpperCase() + colorText.slice(1);

            //   qrCodeText.innerHTML = `Scan to see ${capitalizedColor} variant in your space`;
            }

            if (arText) {
              // Update the AR text to include the selected color
              const colorText = selectedColor.toLowerCase();
              const capitalizedColor = colorText.charAt(0).toUpperCase() + colorText.slice(1);
              arText.innerHTML = `View ${capitalizedColor} variant in your space`;
            }
          }
        });
      });

      // Also update on page load if a color is already selected
      const checkedInput = document.querySelector('input[name="Color"]:checked');
      if (checkedInput) {
        const selectedColor = checkedInput.value;
        const qrCodeText = document.querySelector('.flexera-qr-code-text');
        const arText = document.querySelector('.flexera-ar-text');

        if (qrCodeText) {
          const capitalizedColor = selectedColor.charAt(0).toUpperCase() + selectedColor.slice(1).toLowerCase();
        //   qrCodeText.innerHTML = `Scan to see ${capitalizedColor} variant in your space`;
        }

        if (arText) {
          const capitalizedColor = selectedColor.charAt(0).toUpperCase() + selectedColor.slice(1).toLowerCase();
          arText.innerHTML = `View ${capitalizedColor} variant in your space`;
        }
      }
    });